// Live-Status + Result-Box fuer eine laufende Device-Command
// (TQ #5266 Phase 2 Iteration 1).
//
// Iter 1: Polling auf
//   GET /api/org/<slug>/devices/<id>/commands/<cmd_id>/status
// alle 2s bis status in {done, failed, timeout}.
// Iter 2 (separater TQ): WebSocket-Sub auf /ws/org/<slug>/audit-events.

import { useEffect, useRef, useState } from 'react'
import { useAuth } from '@it-sce/ui/contexts/KeycloakAuthContext'

interface CommandStatusResponse {
  command_id: string
  status: 'pending' | 'running' | 'done' | 'failed' | 'timeout'
  exit_code?: number | null
  stdout?: string
  stderr?: string
  duration_ms?: number
  error?: string | null
  command_type?: string
}

interface CommandStatusProps {
  orgSlug: string
  deviceId: string
  commandId: string
  onDone?: (final: CommandStatusResponse) => void
}

const POLL_MS = 2000
/** Backend cuts off at 5 min; we keep polling for 6 to catch the
 *  failed/timeout transition before giving up. */
const MAX_POLLS = 180

export function CommandStatus({
  orgSlug,
  deviceId,
  commandId,
  onDone,
}: CommandStatusProps) {
  const { api } = useAuth()
  const [state, setState] = useState<CommandStatusResponse | null>(null)
  const [secondsElapsed, setSecondsElapsed] = useState(0)
  const [pollErr, setPollErr] = useState<string | null>(null)
  const [collapsed, setCollapsed] = useState(false)
  const pollCount = useRef(0)
  const onDoneRef = useRef(onDone)
  onDoneRef.current = onDone

  useEffect(() => {
    if (!api) return
    let cancelled = false
    const tick = async () => {
      if (cancelled) return
      pollCount.current += 1
      if (pollCount.current > MAX_POLLS) {
        setPollErr('Status-Polling abgebrochen (>6 Min ohne Abschluss).')
        return
      }
      try {
        const r = await api.get<CommandStatusResponse>(
          `/api/org/${orgSlug}/devices/${deviceId}/commands/${commandId}/status`,
        )
        if (cancelled || !r.data) return
        setState(r.data)
        setPollErr(null)
        if (['done', 'failed', 'timeout'].includes(r.data.status)) {
          onDoneRef.current?.(r.data)
          return // stop polling
        }
      } catch (e) {
        if (cancelled) return
        const err = e as { response?: { status?: number } }
        // 404 in den ersten paar Polls = backend hat den Command noch
        // nicht persisted; weiter pollen.
        if (err.response?.status !== 404) {
          setPollErr('Status konnte nicht geladen werden — weiter polling…')
        }
      }
      if (!cancelled) {
        window.setTimeout(tick, POLL_MS)
      }
    }
    void tick()

    const secTick = window.setInterval(() => {
      setSecondsElapsed((s) => s + 1)
    }, 1000)

    return () => {
      cancelled = true
      window.clearInterval(secTick)
    }
  }, [api, orgSlug, deviceId, commandId])

  if (!state) {
    return (
      <div style={boxStyle('#eff6ff', '#bfdbfe')}>
        <div style={{ fontSize: '0.8125rem', color: '#1e40af' }}>
          ⟳ Warte auf Backend… ({secondsElapsed}s)
        </div>
        {pollErr && (
          <div style={{ marginTop: '0.25rem', fontSize: '0.75rem', color: '#dc2626' }}>
            {pollErr}
          </div>
        )}
      </div>
    )
  }

  const terminal = ['done', 'failed', 'timeout'].includes(state.status)
  const ok = state.status === 'done'

  return (
    <div
      style={boxStyle(
        ok ? '#f0fdf4' : terminal ? '#fef2f2' : '#eff6ff',
        ok ? '#bbf7d0' : terminal ? '#fecaca' : '#bfdbfe',
      )}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <strong style={{ fontSize: '0.875rem', color: terminal ? (ok ? '#166534' : '#991b1b') : '#1e40af' }}>
          {statusLabel(state.status, secondsElapsed, state.duration_ms)}
        </strong>
        {terminal && (
          <button
            onClick={() => setCollapsed((c) => !c)}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: '#6b7280',
              fontSize: '0.8125rem',
            }}
          >
            {collapsed ? 'Details ▾' : 'Details ▴'}
          </button>
        )}
      </div>

      {state.command_type && (
        <div style={{ marginTop: '0.25rem', fontSize: '0.75rem', color: '#6b7280' }}>
          {state.command_type}
          {state.exit_code != null && state.exit_code !== 0
            ? ` · exit ${state.exit_code}`
            : ''}
        </div>
      )}

      {terminal && !collapsed && (
        <div style={{ marginTop: '0.75rem' }}>
          {state.error && (
            <OutputBlock title="Fehler" content={state.error} kind="error" />
          )}
          {state.stdout && (
            <OutputBlock title="stdout" content={state.stdout} kind="stdout" />
          )}
          {state.stderr && (
            <OutputBlock title="stderr" content={state.stderr} kind="stderr" />
          )}
          {!state.error && !state.stdout && !state.stderr && (
            <div style={{ fontSize: '0.8125rem', color: '#6b7280' }}>
              Keine Ausgabe.
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function statusLabel(
  status: CommandStatusResponse['status'],
  seconds: number,
  durationMs?: number,
): string {
  switch (status) {
    case 'pending':
      return `… ausstehend (${seconds}s)`
    case 'running':
      return `▶ läuft (${seconds}s)`
    case 'done':
      return durationMs != null
        ? `✓ fertig (${Math.round(durationMs / 1000)}s)`
        : '✓ fertig'
    case 'failed':
      return '✗ fehlgeschlagen'
    case 'timeout':
      return '⏱ Timeout'
  }
}

function OutputBlock({
  title,
  content,
  kind,
}: {
  title: string
  content: string
  kind: 'stdout' | 'stderr' | 'error'
}) {
  const onCopy = () => {
    void navigator.clipboard.writeText(content)
  }
  const bg = kind === 'error' ? '#fee2e2' : kind === 'stderr' ? '#fef3c7' : '#f9fafb'
  return (
    <div style={{ marginBottom: '0.5rem' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '0.25rem',
        }}
      >
        <span style={{ fontSize: '0.75rem', color: '#6b7280', fontWeight: 600 }}>
          {title}
        </span>
        <button
          onClick={onCopy}
          style={{
            background: 'none',
            border: 'none',
            color: '#2563eb',
            cursor: 'pointer',
            fontSize: '0.75rem',
          }}
        >
          Kopieren
        </button>
      </div>
      <pre
        style={{
          margin: 0,
          padding: '0.5rem',
          background: bg,
          border: '1px solid #e5e7eb',
          borderRadius: 6,
          fontSize: '0.75rem',
          fontFamily:
            "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
          maxHeight: 280,
          overflowY: 'auto',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
        }}
      >
        {content}
      </pre>
    </div>
  )
}

function boxStyle(bg: string, border: string): React.CSSProperties {
  return {
    marginTop: '1rem',
    padding: '0.75rem',
    background: bg,
    border: `1px solid ${border}`,
    borderRadius: 8,
  }
}
